import React, { useState, useEffect } from 'react';
import {
  Container,
  VStack,
  HStack,
  Box,
  Heading,
  Text,
  Button,
  Badge,
  Divider,
  Avatar,
  Grid,
  GridItem,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Card,
  CardBody,
  Flex,
  Icon,
  Stat,
  StatLabel,
  StatNumber,
} from '@chakra-ui/react';
import { 
  FaMapMarkerAlt, 
  FaCalendarAlt, 
  FaShieldAlt,
  FaEnvelope,
  FaHeart,
  FaShare,
  FaFlag 
} from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';

import ImageGallery from '../components/ImageGallery';
import MessageInterface from '../components/MessageInterface';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorDisplay from '../components/ErrorDisplay';
import { Post, User, Category, Brand } from '../types';
import { 
  mockPosts, 
  getUserById, 
  getCategoryById, 
  getBrandById,
  getPostsBySeller 
} from '../data/mockData';

const PostDetailPage: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [seller, setSeller] = useState<User | null>(null);
  const [category, setCategory] = useState<Category | null>(null);
  const [brand, setBrand] = useState<Brand | null>(null);
  const [sellerPosts, setSellerPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [messageData, setMessageData] = useState<{postId: string, sellerId: string} | null>(null);

  const { isOpen: isMessageOpen, onOpen: onMessageOpen, onClose: onMessageClose } = useDisclosure();
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const loadPostData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 800));

        if (!postId) {
          setError('Post ID not provided');
          return;
        }

        const foundPost = mockPosts.find(p => p.id === postId);
        if (!foundPost) {
          setError('Post not found');
          return;
        }

        setPost(foundPost);
        
        // Load related data
        const postSeller = getUserById(foundPost.sellerId);
        const postCategory = getCategoryById(foundPost.categoryId);
        const postBrand = getBrandById(foundPost.brandId);
        const otherPosts = getPostsBySeller(foundPost.sellerId).filter(p => p.id !== postId);

        setSeller(postSeller || null);
        setCategory(postCategory || null);
        setBrand(postBrand || null);
        setSellerPosts(otherPosts);

        // Simulate checking if favorited
        setIsFavorited(Math.random() > 0.7);

      } catch (err) {
        setError('Failed to load post details');
      } finally {
        setLoading(false);
      }
    };

    loadPostData();
  }, [postId]);

  const handleContactSeller = () => {
    if (post && seller) {
      setMessageData({ postId: post.id, sellerId: seller.id });
    }
    onMessageOpen();
  };

  const handleToggleFavorite = () => {
    setIsFavorited(!isFavorited);
    toast({
      title: isFavorited ? 'Removed from favorites' : 'Added to favorites',
      status: 'success',
      duration: 2000,
    });
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast({
      title: 'Link copied!',
      description: 'Post link copied to clipboard',
      status: 'success',
      duration: 2000,
    });
  };

  const handleReport = () => {
    toast({
      title: 'Post reported',
      description: 'Thank you for reporting. We will review this post.',
      status: 'info',
      duration: 3000,
    });
  };

  const handleSendMessage = () => {
    toast({
      title: 'Message sent!',
      description: 'Your message has been sent to the seller.',
      status: 'success',
      duration: 3000,
    });
    onMessageClose();
  };

  const getConditionColor = (condition: string) => {
    switch (condition) {
      case 'new': return 'green';
      case 'like-new': return 'blue';
      case 'excellent': return 'cyan';
      case 'good': return 'orange';
      case 'fair': return 'yellow';
      case 'poor': return 'red';
      default: return 'gray';
    }
  };

  if (loading) {
    return (
      <Container maxW="7xl" py={8}>
        <LoadingSpinner text="Loading post details..." />
      </Container>
    );
  }

  if (error || !post) {
    return (
      <Container maxW="7xl" py={8}>
        <ErrorDisplay
          type="notFound"
          message={error || 'Post not found'}
          onRetry={() => window.location.reload()}
          onGoHome={() => navigate('/')}
        />
      </Container>
    );
  }

  return (
    <Container maxW="7xl" py={8}>
      <VStack spacing={8} align="stretch">
        {/* Breadcrumb */}
        <Breadcrumb>
          <BreadcrumbItem>
            <BreadcrumbLink onClick={() => navigate('/')}>Home</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink onClick={() => navigate('/browse')}>Browse</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink onClick={() => navigate(`/browse?category=${category?.id}`)}>
              {category?.name}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink>{post.title}</BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>

        <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={8}>
          {/* Left Column - Images and Details */}
          <GridItem>
            <VStack spacing={6} align="stretch">
              {/* Image Gallery */}
              <ImageGallery images={post.images} />

              {/* Post Details */}
              <Card>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    <Box>
                      <Heading size="xl" mb={2}>{post.title}</Heading>
                      <HStack spacing={2} mb={4}>
                        <Badge colorScheme={getConditionColor(post.condition)} size="lg">
                          {post.condition.toUpperCase()}
                        </Badge>
                        <Badge colorScheme="green" size="lg">
                          {post.status.toUpperCase()}
                        </Badge>
                      </HStack>
                      <Text fontSize="3xl" fontWeight="bold" color="brand.green.primary">
                        ${post.price.toFixed(2)}
                      </Text>
                    </Box>

                    <Divider />

                    <Box>
                      <Heading size="md" mb={3}>Description</Heading>
                      <Text fontSize="lg" lineHeight="1.6">
                        {post.description}
                      </Text>
                    </Box>

                    <Divider />

                    <Box>
                      <Heading size="md" mb={3}>Details</Heading>
                      <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                        <Stat>
                          <StatLabel>Category</StatLabel>
                          <StatNumber fontSize="lg">{category?.name}</StatNumber>
                        </Stat>
                        <Stat>
                          <StatLabel>Brand</StatLabel>
                          <StatNumber fontSize="lg">{brand?.name}</StatNumber>
                        </Stat>
                        <Stat>
                          <StatLabel>Location</StatLabel>
                          <StatNumber fontSize="lg">
                            <Icon as={FaMapMarkerAlt} mr={2} />
                            {post.location}
                          </StatNumber>
                        </Stat>
                        <Stat>
                          <StatLabel>Posted</StatLabel>
                          <StatNumber fontSize="lg">
                            <Icon as={FaCalendarAlt} mr={2} />
                            {new Date(post.createdAt).toLocaleDateString()}
                          </StatNumber>
                        </Stat>
                      </Grid>
                    </Box>
                  </VStack>
                </CardBody>
              </Card>
            </VStack>
          </GridItem>

          {/* Right Column - Seller Info and Actions */}
          <GridItem>
            <VStack spacing={6} align="stretch">
              {/* Seller Information */}
              <Card>
                <CardBody>
                  <VStack spacing={4}>
                    <HStack spacing={4} align="center" w="full">
                      <Avatar
                        size="lg"
                        name={`${seller?.firstName} ${seller?.lastName}`}
                        src={`https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face`}
                      />
                      <VStack align="start" spacing={1} flex={1}>
                        <Text fontWeight="bold" fontSize="lg">
                          {seller?.firstName} {seller?.lastName}
                        </Text>
                        <HStack>
                          {seller?.isVerified && (
                            <Badge colorScheme="green" variant="subtle">
                              <Icon as={FaShieldAlt} mr={1} />
                              Verified
                            </Badge>
                          )}
                          <Badge colorScheme="blue" variant="subtle">
                            {seller?.sellerStatus}
                          </Badge>
                        </HStack>
                        <Text fontSize="sm" color="gray.600">
                          Joined {new Date(seller?.joinDate || '').toLocaleDateString()}
                        </Text>
                      </VStack>
                    </HStack>

                    <Text fontSize="sm" color="gray.600" textAlign="center">
                      {sellerPosts.length} other item{sellerPosts.length !== 1 ? 's' : ''} for sale
                    </Text>
                  </VStack>
                </CardBody>
              </Card>

              {/* Action Buttons */}
              <VStack spacing={3}>
                <Button
                  colorScheme="green"
                  size="lg"
                  w="full"
                  leftIcon={<FaEnvelope />}
                  onClick={handleContactSeller}
                >
                  Contact Seller
                </Button>

                <HStack w="full" spacing={2}>
                  <Button
                    variant="outline"
                    flex={1}
                    leftIcon={<FaHeart />}
                    colorScheme={isFavorited ? 'red' : 'gray'}
                    onClick={handleToggleFavorite}
                  >
                    {isFavorited ? 'Favorited' : 'Favorite'}
                  </Button>
                  <Button
                    variant="outline"
                    flex={1}
                    leftIcon={<FaShare />}
                    onClick={handleShare}
                  >
                    Share
                  </Button>
                </HStack>

                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<FaFlag />}
                  onClick={handleReport}
                  colorScheme="red"
                >
                  Report this listing
                </Button>
              </VStack>

              {/* Other Items from Seller */}
              {sellerPosts.length > 0 && (
                <Card>
                  <CardBody>
                    <Heading size="md" mb={4}>
                      More from {seller?.firstName}
                    </Heading>
                    <VStack spacing={3}>
                      {sellerPosts.slice(0, 3).map((sellerPost) => (
                        <Flex
                          key={sellerPost.id}
                          w="full"
                          p={3}
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="md"
                          cursor="pointer"
                          _hover={{ bg: 'gray.50' }}
                          onClick={() => navigate(`/post/${sellerPost.id}`)}
                        >
                          <Box
                            w="60px"
                            h="60px"
                            borderRadius="md"
                            bg="gray.200"
                            backgroundImage={`url(${sellerPost.images[0]})`}
                            backgroundSize="cover"
                            backgroundPosition="center"
                            mr={3}
                          />
                          <VStack align="start" spacing={1} flex={1}>
                            <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                              {sellerPost.title}
                            </Text>
                            <Text fontSize="sm" color="brand.green.primary" fontWeight="bold">
                              ${sellerPost.price.toFixed(2)}
                            </Text>
                          </VStack>
                        </Flex>
                      ))}
                    </VStack>
                  </CardBody>
                </Card>
              )}
            </VStack>
          </GridItem>
        </Grid>

        {/* Message Modal */}
        <Modal isOpen={isMessageOpen} onClose={onMessageClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Message Seller</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {messageData && (
                <MessageInterface
                  postId={messageData.postId}
                  currentUserId="2"
                  otherUserId={seller?.id || ''}
                  onSendMessage={handleSendMessage}
                />
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      </VStack>
    </Container>
  );
};

export default PostDetailPage; 